import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { trackAddToCart } from '@/utils/analytics';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  category_id: string;
  quantity: number;
  service_mode: string;
  process_time?: number;
  express_wash_selected?: boolean;
  express_wash_amount?: number;
  user_id?: any;
  variant?: string;
}

interface CommonState {
  cartItems: CartItem[];
}

const loadCartFromLocalStorage = (): CartItem[] => {
  try {
    const data = localStorage.getItem('cart');
    const parsed = data ? JSON.parse(data) : [];

    if (
      Array.isArray(parsed) &&
      parsed.every(
        (item) =>
          typeof item === 'object' &&
          typeof item.id === 'string' &&
          typeof item.name === 'string' &&
          typeof item.price === 'number' &&
          typeof item.image === 'string' &&
          typeof item.category_id === 'string' &&
          typeof item.quantity === 'number'
      )
    ) {
      return parsed;
    }

    return [];
  } catch (e) {
    return [];
  }
};

const initialState: CommonState = {
  cartItems: loadCartFromLocalStorage(),
};

const CartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addCartItem: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.cartItems.find((item: any) => {
        return item.id === action.payload.id;
      });

      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
      } else {
        state.cartItems.push(action.payload);
      }

      trackAddToCart({
        id: action.payload.id,
        name: action.payload.name,
        category_id: action.payload.category_id,
        price: action.payload.price,
        quantity: action.payload.quantity,
        variant: action.payload.variant,
      });

      localStorage.setItem('cart', JSON.stringify([...state.cartItems]));
    },

    removeCartItem: (state, action: PayloadAction<string>) => {
      state.cartItems = state.cartItems.filter(
        (item) => item.id !== action.payload
      );
      localStorage.setItem('cart', JSON.stringify([...state.cartItems]));
    },

    increaseQuantity: (state, action: PayloadAction<string>) => {
      const item = state.cartItems.find((item) => item.id === action.payload);
      if (item) {
        item.quantity += 1;
        localStorage.setItem('cart', JSON.stringify([...state.cartItems]));
      }
    },

    decreaseQuantity: (state, action: PayloadAction<string>) => {
      const itemIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload
      );
      if (itemIndex !== -1) {
        const item = state.cartItems[itemIndex];
        if (item.quantity > 1) {
          item.quantity -= 1;
        } else {
          state.cartItems.splice(itemIndex, 1);
        }
        localStorage.setItem('cart', JSON.stringify([...state.cartItems]));
      }
    },

    updateQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const item = state.cartItems.find((item) => item.id === action.payload.id);
      if (item) {
        item.quantity = Math.max(1, action.payload.quantity);
        localStorage.setItem('cart', JSON.stringify([...state.cartItems]));
      }
    },

    clearCart: (state) => {
      state.cartItems = [];
      localStorage.removeItem('cart');
    },
  },
});

export const {
  addCartItem,
  removeCartItem,
  increaseQuantity,
  decreaseQuantity,
  updateQuantity,
  clearCart,
} = CartSlice.actions;

export default CartSlice.reducer;
